import { apiClient } from './apiClient';

const IMAGE_UPLOAD_MAX_DIMENSION = 1920;
const IMAGE_UPLOAD_OPTIMIZE_THRESHOLD = 1024 * 1024;

const isOptimizableImage = (file: File) => (
  file.type.startsWith('image/') &&
  file.type !== 'image/gif' &&
  file.type !== 'image/svg+xml'
);

const loadImageForUpload = (file: File) => new Promise<HTMLImageElement>((resolve, reject) => {
  const objectUrl = URL.createObjectURL(file);
  const image = new Image();
  image.onload = () => {
    URL.revokeObjectURL(objectUrl);
    resolve(image);
  };
  image.onerror = () => {
    URL.revokeObjectURL(objectUrl);
    reject(new Error('Failed to load image for upload'));
  };
  image.src = objectUrl;
});

const canvasToBlob = (canvas: HTMLCanvasElement, type: string, quality?: number) => new Promise<Blob | null>((resolve) => {
  canvas.toBlob(resolve, type, quality);
});

const optimizeImageForUpload = async (file: File): Promise<File> => {
  if (!isOptimizableImage(file)) {
    return file;
  }

  const image = await loadImageForUpload(file);
  const longestSide = Math.max(image.width, image.height);
  if (longestSide <= IMAGE_UPLOAD_MAX_DIMENSION && file.size <= IMAGE_UPLOAD_OPTIMIZE_THRESHOLD) {
    return file;
  }

  const scale = Math.min(1, IMAGE_UPLOAD_MAX_DIMENSION / longestSide);
  const outputWidth = Math.max(1, Math.round(image.width * scale));
  const outputHeight = Math.max(1, Math.round(image.height * scale));
  const canvas = document.createElement('canvas');
  canvas.width = outputWidth;
  canvas.height = outputHeight;
  const context = canvas.getContext('2d');

  if (!context) {
    return file;
  }

  context.drawImage(image, 0, 0, outputWidth, outputHeight);

  const outputType = file.type === 'image/png' ? 'image/png' : file.type === 'image/webp' ? 'image/webp' : 'image/jpeg';
  const optimizedBlob = await canvasToBlob(
    canvas,
    outputType,
    outputType === 'image/png' ? undefined : 0.88
  );

  if (!optimizedBlob || optimizedBlob.size >= file.size) {
    return file;
  }

  const extension = outputType === 'image/png' ? '.png' : outputType === 'image/webp' ? '.webp' : '.jpg';
  const baseName = file.name.replace(/\.[^.]+$/, '') || `upload_${Date.now()}`;
  return new File([optimizedBlob], `${baseName}${extension}`, {
    type: outputType,
    lastModified: file.lastModified
  });
};

// === Simple upload (uses /media/confirm — no moderation/thumbnails) ===

interface ConfirmUploadResponse {
  uploadId: string;
  fileUrl: string;
  fileKey: string;
  mimeType: string;
  fileSize: number;
}

export const mediaUploadService = {
  async uploadFile(file: Blob, filename?: string, contentType?: string) {
    const resolvedContentType = contentType || (file as File).type || 'application/octet-stream';
    const resolvedFilename = filename || (file as File).name || `upload_${Date.now()}`;
    const fileSize = file.size ?? 0;

    const uploadUrlRes = await apiClient.post<{ uploadUrl: string; uploadId: string }>('/api/v1/posts/media/upload-url', {
      filename: resolvedFilename,
      contentType: resolvedContentType,
      fileSize
    });

    if (!uploadUrlRes.success || !uploadUrlRes.data?.uploadUrl || !uploadUrlRes.data?.uploadId) {
      throw new Error(uploadUrlRes.error || 'Failed to get upload URL');
    }

    const response = await fetch(uploadUrlRes.data.uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': resolvedContentType },
      body: file
    });
    if (!response.ok) throw new Error(`OSS upload failed (${response.status})`);

    const confirmRes = await apiClient.post<ConfirmUploadResponse>('/api/v1/posts/media/confirm', {
      uploadId: uploadUrlRes.data.uploadId
    });

    if (!confirmRes.success || !confirmRes.data?.fileUrl) {
      throw new Error(confirmRes.error || 'Failed to confirm upload');
    }

    return confirmRes.data.fileUrl;
  }
};

// === Full upload with progress (uses /media/process — includes moderation/thumbnails/transcode) ===

export type UploadStatus = 'idle' | 'requesting' | 'uploading' | 'processing' | 'completed' | 'failed';

export interface UploadProgress {
  status: UploadStatus;
  percent: number;
  error?: string;
  mediaId?: string;
}

export type ProgressCallback = (progress: UploadProgress) => void;
export interface UploadMediaOptions {
  displayOrder?: number;
  altText?: string;
}

export interface UploadMediaResult {
  mediaId?: string;
  fileUrl?: string;
  thumbnailUrl?: string;
  mediaType: 'video' | 'image';
}

export async function uploadMedia(
  file: File,
  postId: string | undefined,
  onProgress: ProgressCallback,
  options: UploadMediaOptions = {}
): Promise<UploadMediaResult | null> {
  try {
    const uploadFile = await optimizeImageForUpload(file);
    onProgress({ status: 'requesting', percent: 0 });
    const urlRes = await apiClient.post<{ uploadUrl: string; fileKey: string; uploadId: string }>(
      '/api/v1/posts/media/upload-url', {
        filename: uploadFile.name,
        contentType: uploadFile.type,
        fileSize: uploadFile.size,
        postId
      }
    );

    if (!urlRes.success || !urlRes.data) {
      onProgress({ status: 'failed', percent: 0, error: urlRes.error || 'Failed to get upload URL' });
      return null;
    }

    const { uploadUrl, uploadId } = urlRes.data;

    onProgress({ status: 'uploading', percent: 0 });
    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('PUT', uploadUrl, true);
      xhr.setRequestHeader('Content-Type', uploadFile.type);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          onProgress({ status: 'uploading', percent: Math.round((e.loaded / e.total) * 100) });
        }
      };
      xhr.onload = () => xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`Upload failed: ${xhr.status}`));
      xhr.onerror = () => reject(new Error('Upload network error'));
      xhr.send(uploadFile);
    });

    onProgress({ status: 'processing', percent: 100 });
    const mediaType = uploadFile.type.startsWith('video/') ? 'video' : 'image';
    const processRes = await apiClient.post<{ mediaId?: string; status: string; uploadId?: string }>('/api/v1/posts/media/process', {
      uploadId,
      ...(postId ? { postId } : {}),
      mediaType,
      displayOrder: options.displayOrder,
      altText: options.altText
    });

    if (!processRes.success || !processRes.data) {
      onProgress({ status: 'failed', percent: 100, error: processRes.error || 'Processing failed' });
      return null;
    }

    const pollStart = Date.now();
    while (Date.now() - pollStart < 5 * 60 * 1000) {
      const statusRes = await getUploadStatus(uploadId);
      if (statusRes.success && statusRes.data) {
        if (statusRes.data.status === 'failed') {
          onProgress({
            status: 'failed',
            percent: statusRes.data.progress || 0,
            error: statusRes.data.error || 'Processing failed'
          });
          return null;
        }

        if (statusRes.data.status === 'completed') {
          onProgress({
            status: 'completed',
            percent: 100,
            mediaId: statusRes.data.mediaId || processRes.data.mediaId
          });
          return {
            mediaId: statusRes.data.mediaId || processRes.data.mediaId,
            fileUrl: statusRes.data.fileUrl,
            thumbnailUrl: statusRes.data.thumbnailUrl,
            mediaType
          };
        }

        onProgress({
          status: 'processing',
          percent: Math.max(50, statusRes.data.progress || 50)
        });
      }

      await new Promise((resolve) => window.setTimeout(resolve, 500));
    }

    onProgress({ status: 'failed', percent: 100, error: 'Processing timeout' });
    return null;
  } catch (error: any) {
    onProgress({ status: 'failed', percent: 0, error: error.message || 'Upload failed' });
    return null;
  }
}

export async function getUploadStatus(uploadId: string) {
  return apiClient.get<{
    uploadId: string;
    status: string;
    progress: number;
    mediaId?: string;
    fileUrl?: string;
    thumbnailUrl?: string;
    mediaType?: 'video' | 'image';
    error?: string;
  }>(
    `/api/v1/posts/media/status/${uploadId}`
  );
}
